import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { InvoiceStatusBadge } from "@/components/invoices/InvoiceStatusBadge";
import { getInvoiceCategory, getInvoiceCategoryColor } from "@/lib/invoiceCategories";
import { getStatusTooltip } from "@/lib/invoiceStatus";

/**
 * The category dot. Where the queue allows reclassifying, it is a button that
 * opens the type picker — the same click target the old app had; elsewhere (the
 * mobile list) it is decoration beside the invoice number that already names it.
 */
export function CategoryMark({ invoice, onSelect }) {
  const name = invoice.categoryName ?? getInvoiceCategory(invoice.categoryId)?.name ?? "Unclassified";
  const style = { backgroundColor: getInvoiceCategoryColor(invoice.categoryId) };

  if (!onSelect) {
    return <span className="category-mark shrink-0" style={style} title={name} aria-hidden="true" />;
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            className="category-mark category-mark-button shrink-0"
            style={style}
            aria-label={`Invoice type: ${name}. Change it.`}
            onClick={(event) => {
              event.stopPropagation();
              onSelect(invoice);
            }}
          />
        }
      />
      <TooltipContent>{`${name} · Click to change`}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A status badge, which for processing status doubles as the control that
 * changes it — `PUT /invoices/:guid/status` only sets the processing status, so
 * only that column is clickable. Review status moves through review actions, not
 * by being picked from a list, so it renders as a plain badge.
 *
 * Without a handler (no permission) the processing badge is plain too, rather
 * than a dead button.
 */
export function StatusCell({ invoice, kind, onSelect }) {
  if (!onSelect) return <InvoiceStatusBadge invoice={invoice} kind={kind} compact />;

  // The tooltip replaces the badge's own `title`, so the hover says it can be
  // clicked, like the category dot does, instead of only naming the status.
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            className="invoice-status-trigger"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(invoice);
            }}
          />
        }
      >
        <InvoiceStatusBadge invoice={invoice} kind={kind} compact nativeTitle={false} />
        <span className="sr-only">Change processing status</span>
      </TooltipTrigger>
      <TooltipContent>{`${getStatusTooltip(invoice, kind)} · Click to change`}</TooltipContent>
    </Tooltip>
  );
}
